import { PropsWithChildren, useEffect, useRef } from 'react';
import { LDContext } from 'launchdarkly-js-client-sdk';
import { LDProvider, useLDClient } from 'launchdarkly-react-client-sdk';

import { LAUNCH_DARKLY_ANONYMOUS_KEY } from 'app/shared/types/feature-flags.types';
import { useBuilding } from 'app/shared/contexts/building';
import { useOpenParams } from 'app/shared/hooks/use-open-params/use-open-params.hook';
import { useUser } from 'app/shared/contexts/user';

const ANONYMOUS_CONTEXT: LDContext = {
  kind: 'user',
  key: LAUNCH_DARKLY_ANONYMOUS_KEY,
  anonymous: true,
};

const buildLDContext = (
  email: string | undefined,
  buildingUuid: string | undefined,
  appBrand: string,
): LDContext => {
  if (!email || !buildingUuid) return ANONYMOUS_CONTEXT;
  return {
    kind: 'user',
    key: email,
    email,
    anonymous: false,
    buildingUUIDs: [buildingUuid],
    appBrand,
  };
};

/**
 * Re-identifies the LaunchDarkly client when the user/building/appBrand land.
 * `ldClient.identify()` updates the evaluated flags without remounting the
 * Provider — important because remounting would flash defaults at consumers.
 */
const LDContextSync = ({ children }: PropsWithChildren): JSX.Element => {
  const ldClient = useLDClient();
  const { user } = useUser();
  const { building } = useBuilding();
  const { appBrand } = useOpenParams();
  const identifiedRef = useRef(false);

  useEffect(() => {
    if (!ldClient || identifiedRef.current) return;
    if (!user?.email || !building?.uuid) return;
    identifiedRef.current = true;
    ldClient.identify(buildLDContext(user.email, building.uuid, appBrand));
  }, [ldClient, user?.email, building?.uuid, appBrand]);

  // Fragment is unavoidable: this component renders its children as-is and
  // TS requires a JSX.Element. Side effect is the identify() call above.
  // eslint-disable-next-line react/jsx-no-useless-fragment
  return <>{children}</>;
};

/**
 * Bootstraps LaunchDarkly with an anonymous context, then re-identifies once
 * the user/building data is available. Uses `LDProvider` directly (not
 * `asyncWithLDProvider`) so children render immediately — `useFlags()`
 * returns defaults until LD finishes initializing. Avoids the spinner-flash
 * that blocked Cypress earlier and matches the non-blocking behaviour of
 * the old FeatureFlagsProvider.
 *
 * Uses `LDContext` (not the deprecated `LDUser` shape) per
 * launchdarkly-js-client-sdk v3+.
 */
export const LaunchDarklyProvider = ({ children }: PropsWithChildren): JSX.Element => (
  <LDProvider
    clientSideID={process.env.REACT_APP_TENANT_LAUNCH_DARKLY_CLIENT_ID ?? ''}
    context={ANONYMOUS_CONTEXT}
    timeout={2}
  >
    <LDContextSync>{children}</LDContextSync>
  </LDProvider>
);
